import {MiddlewareType} from 'middlewares'
import {Component} from 'middlewares/sideEffects/analytics'
import {getPiccCardCountry} from 'modules/meta/selectors'
import {clearPiccCardCountry} from 'modules/meta/slice'
import {CARD_CURRENCY_PARAM} from 'modules/offerCheck/contants'
import {
  getIsPiccOfferCheckLoading,
  getPiccErrorContext
} from 'modules/offerCheck/selectors'
import {PiccErrorType} from 'modules/offerCheck/types'
import {syncOfferCheckTokenToUrl} from 'modules/offerCheck/utils'
import {validateOfferCheckUrl} from 'modules/offerCheck/validation'
import {requiredParamsMor} from 'modules/offerCheck/validation'
import logBoFHService, {
  BoFHServiceEndpointNames,
  BoFHServiceStatus
} from 'performance/logBoFHService'
import {Dispatch} from 'redux'
import {logToDataDog} from 'services/datadogLogger'
import {RootState} from 'store'
import timing from 'utils/timing'
import {urlParams} from 'utils/urlParams'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {offerCheckApi} from '@vio/core/src/offer/services/offerCheckApi'
import {logger} from '@vio/core/src/utils/logging/logger'

const requestsTiming = timing()

const getIsPiccCall = (offerCheckUrl: string) =>
  offerCheckUrl.includes(`${CARD_CURRENCY_PARAM}=`)

const trackPiccOfferCheckError = (
  state: RootState,
  dispatch: Dispatch,
  errorContext = getPiccErrorContext(state)
) => {
  if (!errorContext) return

  dispatch(
    trackEvent({
      category: Category.System,
      entity: Entity.CurrencySelector,
      action: Action.Errored,
      page: Page.Payment,
      payload: {
        component: Component.PayInCardCurrency,
        cardCountry: getPiccCardCountry(state)
      },
      analyticsContext: {
        [AnalyticsContext.ErrorContext]: errorContext
      }
    })
  )
}

const middleware: MiddlewareType = store => next => action => {
  const {dispatch, getState} = store
  const previousState = getState()

  next(action)

  if (
    clearPiccCardCountry.match(action) &&
    getIsPiccOfferCheckLoading(previousState)
  ) {
    trackPiccOfferCheckError(previousState, dispatch, {
      errorType: PiccErrorType.ClearedBeforeResult
    })
  }

  if (offerCheckApi.endpoints.getOfferCheck.matchFulfilled(action)) {
    // The card-currency (PICC) check carries its own token; only the main
    // check's may go to the page URL.
    if (getIsPiccCall(action.meta.arg.originalArgs.url)) {
      trackPiccOfferCheckError(getState(), dispatch)
    } else {
      syncOfferCheckTokenToUrl(action.payload)
    }
  }

  if (offerCheckApi.endpoints.getOfferCheck.matchPending(action)) {
    const offerCheckUrl = action.meta.arg.originalArgs.url
    const queryParams = urlParams()

    // Checking that the url has at least one of the MOR params. If it does, we need to validate all of them.
    const validateMorParams = requiredParamsMor.some(
      morParam => queryParams[morParam] !== undefined
    )

    const {isValid, validationErrors} = validateOfferCheckUrl(
      offerCheckUrl,
      validateMorParams
    )

    if (!isValid) {
      logToDataDog({
        logName: 'OfferCheckInvalidUrlParams',
        metadata: {
          offerCheckUrl,
          errors: validationErrors
        },
        level: 'error'
      })

      validationErrors.forEach(validationError => {
        logger.warn(validationError.message)
      })
    }

    requestsTiming.start('offerCheck')

    logBoFHService(BoFHServiceEndpointNames.OfferCheck, BoFHServiceStatus.start)
  }

  if (offerCheckApi.endpoints.getOfferCheck.matchFulfilled(action)) {
    const timings = requestsTiming.stop('offerCheck')
    logBoFHService(
      BoFHServiceEndpointNames.OfferCheck,
      BoFHServiceStatus.done,
      {timings, request_id: action?.payload?.httpRequestId}
    )
  }

  if (offerCheckApi.endpoints.getOfferCheck.matchRejected(action)) {
    if (getIsPiccCall(action.meta.arg.originalArgs.url)) {
      trackPiccOfferCheckError(getState(), dispatch)
    }

    const isFatalError = Number(action.error.code) >= 500
    const timings = requestsTiming.stop('offerCheck')

    logBoFHService(
      BoFHServiceEndpointNames.OfferCheck,
      isFatalError ? BoFHServiceStatus.fatalError : BoFHServiceStatus.error,
      {
        error: action?.payload?.data?.details,
        timings
      },
      'error'
    )
  }
}

export default middleware
